import { motion } from 'framer-motion'
import { Reorder } from 'framer-motion'
import PointsCounter from './PointsCounter.jsx'
import Sparkline from './Sparkline.jsx'

const MEDALS = { 1: '👑', 2: '🥈', 3: '🥉' }

export default function LeaderboardRow({ row, isMe = false, dark = false, onClick }) {
  const { participant, points, byDay, currentRank, previousRank } = row
  const movement = previousRank ? previousRank - currentRank : 0

  return (
    <Reorder.Item
      value={row}
      dragListener={false}
      layout
      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
      className="list-none"
    >
      <motion.button
        onClick={onClick}
        whileHover={{ y: -2 }}
        className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-shadow ${
          dark
            ? 'bg-white/5 hover:bg-white/10 border border-white/10'
            : 'bg-white hover:shadow-card-hover border border-brand-border'
        } ${isMe ? (dark ? 'ring-2 ring-brand-blue' : 'ring-2 ring-brand-blue shadow-glow-blue') : ''}`}
      >
        <div className={`flex flex-col items-center justify-center w-10 ${dark ? 'text-white' : 'text-brand-text'}`}>
          {MEDALS[currentRank]
            ? (
              <span
                className={`text-3xl ${currentRank === 1 ? 'animate-crown-shine' : ''}`}
                aria-hidden="true"
              >
                {MEDALS[currentRank]}
              </span>
            )
            : <span className="text-2xl font-bold opacity-60">#{currentRank}</span>
          }
          <MovementBadge movement={movement} dark={dark} />
        </div>

        <Avatar participant={participant} dark={dark} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-semibold truncate ${dark ? 'text-white' : 'text-brand-text'}`}>
              {participant.name}
            </span>
            {isMe && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-blue text-white font-semibold">You</span>
            )}
          </div>
        </div>

        <div className="hidden sm:block w-32">
          <Sparkline data={byDay} color={dark ? '#7AB7FF' : '#3076E2'} height={32} />
        </div>

        <div className="text-right">
          <PointsCounter
            value={points}
            className={`text-2xl font-bold ${dark ? 'text-white' : 'text-brand-text'}`}
          />
          <div className={`text-[10px] uppercase tracking-wider ${dark ? 'text-white/50' : 'text-brand-text-muted'}`}>
            points
          </div>
        </div>
      </motion.button>
    </Reorder.Item>
  )
}

function MovementBadge({ movement, dark }) {
  if (!movement || Math.abs(movement) === 0) {
    return <span className={`text-[9px] mt-0.5 ${dark ? 'text-white/30' : 'text-brand-text-muted'}`}>—</span>
  }
  const up = movement > 0
  return (
    <motion.span
      initial={{ y: up ? 6 : -6, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`text-[10px] mt-0.5 font-bold ${up ? 'text-brand-success' : 'text-brand-danger'}`}
    >
      {up ? '▲' : '▼'} {Math.abs(movement)}
    </motion.span>
  )
}

function Avatar({ participant, dark }) {
  if (participant.avatar_url) {
    return (
      <img
        src={participant.avatar_url}
        alt={participant.name}
        className={`w-12 h-12 rounded-full object-cover border-2 ${dark ? 'border-white/20' : 'border-brand-border'}`}
      />
    )
  }
  const initials = participant.name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?'
  return (
    <div
      className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold ${
        dark ? 'bg-brand-blue text-white' : 'bg-brand-blue text-white'
      }`}
    >
      {initials}
    </div>
  )
}
